import { Sparkles } from 'lucide-react'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { Chip } from '@/components/ui/Chip'
import { Tooltip } from '@/components/ui/Tooltip'
import type { CostEstimate } from '@/api/endpoints/factories'
import styles from './QualityTierSelector.module.scss'

export type QualityTier = 'standard' | 'high'

export interface QualityTierSelectorProps {
  /** Current selection. Controlled — parent owns the state and persists
   * it (e.g. via ``react-hook-form``). Decisão 57: form always defaults
   * to ``'standard'`` on mount — no localStorage. */
  value: QualityTier
  onValueChange: (value: QualityTier) => void
  /** Tiers the active factory's hooks support, straight from the catalog
   * payload. The selector renders ``null`` when this doesn't include
   * ``'high'`` (decisão 53 + 54) — no point in showing a control with one
   * option. */
  supportedTiers: ReadonlyArray<QualityTier>
  /** Per-tier cost estimate from the catalog (decisão 55). Drives the
   * "≈3× tokens" chip's number when ``high`` is selected. ``source``
   * differentiates ``'channel_history'`` (real spend on the active
   * canal) from ``'default'`` (global fallback). */
  costEstimate: CostEstimate
}

const OPTIONS: ReadonlyArray<SegmentedOption<QualityTier>> = [
  { value: 'standard', label: 'Padrão' },
  { value: 'high', label: 'Alta qualidade' },
]

/** Per-component breakdown surfaced inside the warning chip's tooltip
 * (decisão 58). Strings hardcoded — Phase D doesn't introduce a
 * dynamic breakdown API; numbers track decisão 18 of the plan and
 * should be revisited if HQ component pricing drifts.
 *
 * If you change a number here, also update decisão 18 in
 * ``docs/plan-quality-tier.md`` so plan and UI stay in sync. */
const HQ_COST_BREAKDOWN: ReadonlyArray<{ label: string; cost: string }> = [
  { label: 'Ideação Sonnet', cost: '~$0.02' },
  { label: 'Judge Haiku', cost: '~$0.005' },
  { label: 'Web anchoring', cost: '~$0.01' },
  { label: 'Vision picker', cost: '~$0.02' },
  { label: 'Thinking budget', cost: '~$0.03' },
]

/** Format USD into a short display string. We render with 2 decimals
 * because the values are dollars-not-cents and the chip's tight
 * horizontal space rules out scientific notation. */
function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`
}

/**
 * Quality tier picker for factories whose hooks accept ``'high'``.
 *
 * Renders ``null`` for factories that only declare ``['standard']`` in
 * the catalog (decisão 54) so non-viral_shorts forms stay untouched in
 * Phase D. When visible, behaves as a 2-option segmented control;
 * selecting "Alta qualidade" surfaces a warning chip with a per-component
 * cost breakdown in the tooltip (decisão 56).
 *
 * Phase D scope (decisão 53): rendered in the form's main row, not the
 * "Ajuste fino" collapsible — HQ is a conscious cost decision and
 * hiding it would invisibilize the tradeoff. */
export function QualityTierSelector({
  value,
  onValueChange,
  supportedTiers,
  costEstimate,
}: QualityTierSelectorProps) {
  // Decisão 53: render gate is data-driven. If the catalog says this
  // factory's hooks don't accept 'high', show nothing — the form gets
  // back its original layout without any conditional CSS in the parent.
  if (!supportedTiers.includes('high')) return null

  const isHigh = value === 'high'
  const isDefaultSource = costEstimate.source === 'default'

  // Decisão 59: when the channel hasn't accumulated enough samples
  // (samples<5 in at least one tier), the backend returns the global
  // defaults with ``source='default'``. UI tells the user honestly,
  // without changing the number.
  const tooltipBody = (
    <div className={styles.tooltipBody}>
      <div className={styles.tooltipTitle}>HQ adiciona (≈3× tokens):</div>
      <ul className={styles.tooltipList}>
        {HQ_COST_BREAKDOWN.map((row) => (
          <li key={row.label}>
            <span>{row.label}</span>
            <span className={styles.tooltipCost}>{row.cost}</span>
          </li>
        ))}
      </ul>
      <div className={styles.tooltipFootnote}>
        {isDefaultSource
          ? 'Estimativa global — ainda sem histórico suficiente neste canal. Recalibra após 5 gerações em cada tier.'
          : `Média das últimas ${String(costEstimate.samples)} gerações deste canal.`}
      </div>
    </div>
  )

  return (
    <div className={styles.wrap}>
      <Segmented<QualityTier>
        options={OPTIONS}
        value={value}
        onValueChange={onValueChange}
        aria-label="Qualidade da geração"
      />
      {isHigh ? (
        <Tooltip content={tooltipBody} side="top">
          <Chip
            variant="warning"
            leadingIcon={<Sparkles />}
            className={styles.chip}
            // Native title fallback so users without JS hover (touch
            // devices that don't fire pointerenter) still see the gist.
            title="HQ usa ~3× mais tokens — passe o mouse para o detalhamento"
          >
            <span className={styles.chipPrimary}>
              {formatUsd(costEstimate.standard_avg_usd)} → {formatUsd(costEstimate.high_avg_usd)}
            </span>
            <span className={styles.chipSecondary}>≈3× tokens</span>
            {isDefaultSource ? (
              <span className={styles.chipFootnote}>(estimativa global)</span>
            ) : null}
          </Chip>
        </Tooltip>
      ) : null}
    </div>
  )
}
